// @ts-nocheck
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient, facilityGroupService } from '../apiClient';

export interface Tenant {
  id: string;
  name: string;
  code: string;
  type: string;
  isActive: boolean;
  settings?: {
    logo?: string;
    primaryColor?: string;
    features?: string[];
  };
  metadata?: any;
  createdAt: string;
  updatedAt: string;
  facilityGroups?: FacilityGroup[];
}

export interface FacilityGroup {
  id: string;
  name: string;
  code: string;
  description?: string;
  parent_id?: string; // Fix: use snake_case to match database
  is_active: boolean; // Fix: use snake_case to match database
  created_at: string; // Fix: use snake_case to match database
  updated_at: string; // Fix: use snake_case to match database
  type?: string;
  settings?: any;
  metadata?: any;
  facilities?: Facility[];
  children?: FacilityGroup[];
}

export interface Facility {
  id: string;
  name: string;
  code: string;
  facilityGroupId: string;
  type: string;
  isActive: boolean;
  address?: any;
  contactInfo?: any;
}

export interface TenantContextType {
  currentTenant: Tenant | null;
  availableTenants: Tenant[];
  currentFacilityGroup: FacilityGroup | null;
  availableFacilityGroups: FacilityGroup[];
  isLoading: boolean;
  error: string | null;
  switchTenant: (tenantId: string) => Promise<void>;
  switchFacilityGroup: (facilityGroupId: string) => Promise<void>;
  refreshTenants: () => Promise<void>;
  refreshFacilityGroups: () => Promise<void>;
  isMultiTenant: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [currentFacilityGroup, setCurrentFacilityGroup] = useState<FacilityGroup | null>(null);
  const [availableFacilityGroups, setAvailableFacilityGroups] = useState<FacilityGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if we're in a multi-tenant environment
  const isMultiTenant = availableTenants.length > 1;

  const fetchTenants = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch top-level facility groups (which are the "tenants"/customers)
      const facilityGroups = await facilityGroupService.getFacilityGroups();
      const topLevelFacilityGroups = facilityGroups.filter(fg => !fg.parent_id); // Fix: use snake_case field name
      
      // Convert facility groups to tenant format for compatibility
      const tenants: Tenant[] = topLevelFacilityGroups.map(fg => ({
        id: fg.id,
        name: fg.name,
        code: fg.code,
        type: fg.type || 'healthcare',
        isActive: fg.is_active, // Fix: use snake_case field name from database
        createdAt: fg.created_at, // Fix: use snake_case field name from database
        updatedAt: fg.updated_at, // Fix: use snake_case field name from database
        settings: fg.settings,
        metadata: fg.metadata,
      }));

      if (tenants.length === 0) {
        const defaultTenant: Tenant = {
          id: 'default',
          name: 'Default Organization',
          code: 'DEFAULT',
          type: 'healthcare',
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setAvailableTenants([defaultTenant]);
        setCurrentTenant(defaultTenant);
        localStorage.setItem('selectedTenantId', defaultTenant.id);
      } else {
        setAvailableTenants(tenants);
        
        // Set current tenant from localStorage or use first available
        const savedTenantId = localStorage.getItem('selectedTenantId');
        const tenant = tenants.find(t => t.id === savedTenantId) || tenants[0];
        setCurrentTenant(tenant);
        localStorage.setItem('selectedTenantId', tenant.id);
      }
    } catch (err) {
      console.warn('Failed to fetch tenants:', err);
      // Fallback to default tenant
      const defaultTenant: Tenant = {
        id: 'default',
        name: 'Default Organization', 
        code: 'DEFAULT',
        type: 'healthcare',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setAvailableTenants([defaultTenant]);
      setCurrentTenant(defaultTenant);
      localStorage.setItem('selectedTenantId', defaultTenant.id);
      setError('Failed to load tenants, using default');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFacilityGroups = async () => {
    try {
      const facilityGroups = await facilityGroupService.getFacilityGroups();
      setAvailableFacilityGroups(facilityGroups);
      
      // Set current facility group from localStorage or use first available
      const savedFacilityGroupId = localStorage.getItem('selectedFacilityGroupId');
      const facilityGroup = facilityGroups.find(fg => fg.id === savedFacilityGroupId) || facilityGroups[0];
      
      if (facilityGroup) {
        setCurrentFacilityGroup(facilityGroup);
        localStorage.setItem('selectedFacilityGroupId', facilityGroup.id);
      }
    } catch (err) {
      console.warn('Failed to fetch facility groups:', err);
      // Clear facility group if fetch fails
      setCurrentFacilityGroup(null);
      localStorage.removeItem('selectedFacilityGroupId');
    }
  };

  const switchTenant = async (tenantId: string) => {
    const tenant = availableTenants.find(t => t.id === tenantId);
    if (!tenant) {
      throw new Error('Tenant not found');
    }

    setCurrentTenant(tenant);
    localStorage.setItem('selectedTenantId', tenantId);

    // Clear facility group when switching tenants
    setCurrentFacilityGroup(null);
    localStorage.removeItem('selectedFacilityGroupId');

    // Fetch facility groups for the new tenant
    await fetchFacilityGroups();
  };

  const switchFacilityGroup = async (facilityGroupId: string) => {
    const facilityGroup = availableFacilityGroups.find(fg => fg.id === facilityGroupId);
    if (!facilityGroup) {
      throw new Error('Facility group not found');
    }

    setCurrentFacilityGroup(facilityGroup);
    localStorage.setItem('selectedFacilityGroupId', facilityGroupId);
  };

  const refreshTenants = async () => {
    await fetchTenants();
  };

  const refreshFacilityGroups = async () => {
    await fetchFacilityGroups();
  };

  useEffect(() => {
    const initializeContext = async () => {
      await fetchTenants();
      await fetchFacilityGroups();
    };
    initializeContext();
  }, []);

  const contextValue: TenantContextType = {
    currentTenant,
    availableTenants,
    currentFacilityGroup,
    availableFacilityGroups,
    isLoading,
    error,
    switchTenant,
    switchFacilityGroup,
    refreshTenants,
    refreshFacilityGroups,
    isMultiTenant,
  };

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantProvider;